import { ChevronLeft, ChevronRight, RotateCcw } from 'lucide-react';
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import SlideShell from './SlideShell';
import MarkdownRenderer from './MarkdownRenderer';

interface MCQItem {
  id: string;
  question: string;
  options: string[];
  answer: number;
  explanation?: string;
}

interface MCQSlideProps {
  slideNumber: number;
  totalSlides: number;
  title: string;
  subtitle: string;
  content: { items: MCQItem[] };
  lessonTitle?: string;
  lessonThumbnail?: string;
  onNext?: () => void;
  onBack?: () => void;
  onBackToLesson?: () => void;
  isCompleted?: boolean;
  onMarkComplete?: () => void;
}

const MCQSlide = ({
  slideNumber,
  totalSlides,
  title,
  subtitle,
  content,
  lessonTitle,
  lessonThumbnail,
  onNext,
  onBack,
  onBackToLesson,
  isCompleted,
  onMarkComplete,
}: MCQSlideProps) => {
  const items = content.items || [];
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [completedItems, setCompletedItems] = useState<Set<number>>(new Set());
  const [answers, setAnswers] = useState<Record<number, {
    selectedAnswer: number | null;
    showResult: boolean;
  }>>({});

  const currentItem = items[currentItemIndex];
  
  // Find correct answer index - handle both number index and string answer text
  const correctAnswerIndex = typeof currentItem?.answer === 'number' 
    ? currentItem.answer 
    : currentItem?.options.findIndex(option => option === String(currentItem?.answer)) ?? -1;
  
  const isCorrect = selectedAnswer === correctAnswerIndex;
  const allItemsCompleted = completedItems.size === items.length;

  const handleSubmit = () => {
    if (selectedAnswer !== null) {
      setShowResult(true);
      setAnswers({
        ...answers,
        [currentItemIndex]: { selectedAnswer, showResult: true }
      });
      if (isCorrect) {
        setCompletedItems(prev => new Set(prev).add(currentItemIndex));
      }
    }
  };

  const handleNextItem = () => {
    if (currentItemIndex < items.length - 1) {
      setCurrentItemIndex(prev => prev + 1);
    }
  };

  const handlePrevItem = () => {
    if (currentItemIndex > 0) {
      setCurrentItemIndex(prev => prev - 1);
    }
  };

  const handleResetCurrent = () => {
    setSelectedAnswer(null);
    setShowResult(false);
    const newAnswers = { ...answers };
    delete newAnswers[currentItemIndex];
    setAnswers(newAnswers);
    setCompletedItems(prev => {
      const newSet = new Set(prev);
      newSet.delete(currentItemIndex);
      return newSet;
    });
  };

  const handleResetAll = () => {
    setAnswers({});
    setSelectedAnswer(null);
    setShowResult(false);
    setCompletedItems(new Set());
  };

  // Load saved answer when index changes
  useEffect(() => {
    const saved = answers[currentItemIndex];
    setSelectedAnswer(saved?.selectedAnswer ?? null);
    setShowResult(saved?.showResult ?? false);
  }, [currentItemIndex, answers]);

  if (!currentItem) {
    return (
      <div className="h-full flex items-center justify-center">
        <p className="text-muted-foreground">No content available</p>
      </div>
    );
  }

  return (
    <SlideShell
      lessonTitle={lessonTitle}
      lessonThumbnail={lessonThumbnail}
      slideNumber={slideNumber}
      totalSlides={totalSlides}
      subtitle={subtitle}
      onBackToLesson={onBackToLesson}
      onBack={onBack}
      onNext={onNext}
      showMarkComplete={true}
      isCompleted={isCompleted}
      onMarkComplete={allItemsCompleted ? onMarkComplete : undefined}
    >
      <h1 className="font-heading text-2xl mb-6 text-foreground">
        {title}
      </h1>

      {/* Item counter and controls */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-muted-foreground">
          Question {currentItemIndex + 1} of {items.length}
        </p>
        <div className="flex gap-2">
          {showResult && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleResetCurrent}
            >
              <RotateCcw className="w-4 h-4 mr-1" />
              Reset
            </Button>
          )}
          {Object.keys(answers).length > 1 && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleResetAll}
            >
              Reset All
            </Button>
          )}
          {items.length > 1 && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={handlePrevItem}
                disabled={currentItemIndex === 0}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleNextItem}
                disabled={currentItemIndex === items.length - 1}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="mb-6">
        <MarkdownRenderer content={currentItem.question} />
      </div>

      <div className="space-y-3 mb-6">
        {currentItem.options?.map((option, index) => (
          <button
            key={index}
            onClick={() => !showResult && setSelectedAnswer(index)}
            disabled={showResult}
            className={`w-full text-left py-2 px-3 rounded-[15px] text-sm transition-colors ${
              selectedAnswer === index
                ? showResult
                  ? isCorrect
                    ? 'bg-[#27c840] text-white'
                    : 'bg-destructive text-white'
                  : 'bg-primary/10 text-foreground border-2 border-primary'
                : 'bg-white border border-[#f0f0f0] hover:bg-[#fafafa]'
            } ${showResult && index === correctAnswerIndex ? 'bg-[#27c840] text-white' : ''}`}
          >
            <MarkdownRenderer content={option} />
          </button>
        ))}
      </div>

      {showResult && currentItem.explanation && (
        <div className="bg-white rounded-[15px] border border-[#f0f0f0] p-4 mb-6">
          <p className="text-sm font-medium mb-2">
            Explanation:
          </p>
          <MarkdownRenderer content={currentItem.explanation} />
        </div>
      )}

      {!showResult && selectedAnswer !== null && (
        <Button
          onClick={handleSubmit}
          className="bg-[#27c840] hover:bg-[#22b038] text-white rounded-[12px]"
        >
          Submit Answer
        </Button>
      )}
    </SlideShell>
  );
};

export default MCQSlide;

